import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import UserAvatar from '../components/UserAvatar'
import {
  getLeagueTeamCaptainName,
  getLeagueTeamDisplayName,
  getLeagueTeamImageSrc,
  LEAGUE_TEAM_NAMES,
} from '../constants/teamNames'
import './Standings.css'

interface UserStanding {
  id: number
  username: string
  discordId: string | null
  discordUsername: string | null
  discordGlobalName: string | null
  discordAvatar: string | null
  wins: number
  losses: number
  ties: number
  winPercentage: number
  pointsFor: number
  pointsAgainst: number
  totalGames: number
  crystalBalls: number
}

interface UserAssignment {
  id: number
  username: string
  discordId?: string | null
  discordUsername: string | null
  discordGlobalName: string | null
  discordAvatar: string | null
  leagueTeamName: string | null
  groupPlaySlot: string | null
}

interface TeamMemberStanding {
  id: number
  displayName: string
  discordId: string | null
  discordAvatar: string | null
  groupPlaySlot: string | null
  wins: number
  losses: number
  ties: number
  pointDifferential: number
  totalGames: number
  crystalBalls: number
}

interface TeamStanding {
  id: number
  name: string
  playersAssigned: number
  maxPlayers: number
  wins: number
  losses: number
  ties: number
  pointDifferential: number
  crystalBalls: number
  totalGames: number
  winPercentage: number
  members: TeamMemberStanding[]
}

interface TeamProphetBonus {
  teamName: string
  bonusPoints: number
}

type SortKey =
  | 'rank'
  | 'team'
  | 'players'
  | 'wins'
  | 'losses'
  | 'ties'
  | 'crystalBalls'
  | 'pointDifferential'
  | 'totalGames'

type SortDirection = 'asc' | 'desc'
type TeamMemberSortMode = 'performance' | 'draft'

interface SortConfig {
  key: SortKey
  direction: SortDirection
}

function getGroupPlaySlotSortValue(groupPlaySlot: string | null): number {
  if (!groupPlaySlot) {
    return Number.MAX_SAFE_INTEGER
  }

  const [group, player] = groupPlaySlot.split('.').map(Number)
  if (!Number.isFinite(group) || !Number.isFinite(player)) {
    return Number.MAX_SAFE_INTEGER
  }

  return group * 100 + player
}

function getDraftRoundLabel(groupPlaySlot: string | null): string {
  if (!groupPlaySlot) {
    return 'Unassigned'
  }

  const [round] = groupPlaySlot.split('.').map(Number)
  return Number.isFinite(round) ? `Round ${round}` : 'Unassigned'
}

function compareTeamMembersByDraftOrder(a: TeamMemberStanding, b: TeamMemberStanding): number {
  const slotA = getGroupPlaySlotSortValue(a.groupPlaySlot)
  const slotB = getGroupPlaySlotSortValue(b.groupPlaySlot)

  if (slotA !== slotB) {
    return slotA - slotB
  }

  return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
}

function compareTeamMembersByPerformance(a: TeamMemberStanding, b: TeamMemberStanding): number {
  if (b.wins !== a.wins) {
    return b.wins - a.wins
  }
  if (b.crystalBalls !== a.crystalBalls) {
    return b.crystalBalls - a.crystalBalls
  }

  const pctA = a.totalGames > 0 ? a.wins / a.totalGames : 0
  const pctB = b.totalGames > 0 ? b.wins / b.totalGames : 0
  if (pctB !== pctA) {
    return pctB - pctA
  }
  if (b.pointDifferential !== a.pointDifferential) {
    return b.pointDifferential - a.pointDifferential
  }

  return compareTeamMembersByDraftOrder(a, b)
}

function compareDefaultTeamStandings(a: TeamStanding, b: TeamStanding): number {
  if (b.wins !== a.wins) {
    return b.wins - a.wins
  }
  if (b.crystalBalls !== a.crystalBalls) {
    return b.crystalBalls - a.crystalBalls
  }
  if (b.winPercentage !== a.winPercentage) {
    return b.winPercentage - a.winPercentage
  }
  return b.pointDifferential - a.pointDifferential
}

export default function Standings() {
  const navigate = useNavigate()
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([])
  const [userStandings, setUserStandings] = useState<UserStanding[]>([])
  const [prophetBonuses, setProphetBonuses] = useState<TeamProphetBonus[]>([])
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)
  const [teamMemberSortMode, setTeamMemberSortMode] = useState<TeamMemberSortMode>('performance')
  const [expandedTeamImage, setExpandedTeamImage] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadStandings = async () => {
      try {
        const [usersResponse, standingsResponse, bonusesResponse] = await Promise.all([
          api.get<UserAssignment[]>('/users'),
          api.get<UserStanding[]>('/standings'),
          api.get<TeamProphetBonus[]>('/standings/prophet-bonuses'),
        ])

        setUserAssignments(usersResponse.data ?? [])
        setUserStandings(standingsResponse.data ?? [])
        setProphetBonuses(bonusesResponse.data ?? [])
      } catch (error) {
        console.error('Failed to load standings data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadStandings()
  }, [])

  const teamStandings: TeamStanding[] = useMemo(
    () => {
      const standingsByUserId = new Map(userStandings.map(user => [user.id, user]))
      const prophetBonusByTeam = new Map(
        prophetBonuses.map(bonus => [bonus.teamName, bonus.bonusPoints])
      )

      const teams = LEAGUE_TEAM_NAMES.map((name, index) => {
        const assignedUsers = userAssignments.filter(user => user.leagueTeamName === name)
        const members: TeamMemberStanding[] = assignedUsers
          .map(user => {
            const standing = standingsByUserId.get(user.id)
            const displayName =
              user.discordGlobalName ??
              user.discordUsername ??
              user.username

            return {
              id: user.id,
              displayName,
              discordId: standing?.discordId ?? user.discordId ?? null,
              discordAvatar: standing?.discordAvatar ?? user.discordAvatar ?? null,
              groupPlaySlot: user.groupPlaySlot,
              wins: standing?.wins ?? 0,
              losses: standing?.losses ?? 0,
              ties: standing?.ties ?? 0,
              pointDifferential: (standing?.pointsFor ?? 0) - (standing?.pointsAgainst ?? 0),
              totalGames: standing?.totalGames ?? 0,
              crystalBalls: standing?.crystalBalls ?? 0,
            }
          })
          .sort(
            teamMemberSortMode === 'performance'
              ? compareTeamMembersByPerformance
              : compareTeamMembersByDraftOrder
          )

        const wins = members.reduce((sum, member) => sum + member.wins, 0)
        const losses = members.reduce((sum, member) => sum + member.losses, 0)
        const ties = members.reduce((sum, member) => sum + member.ties, 0)
        const totalGames = members.reduce((sum, member) => sum + member.totalGames, 0)
        const pointDifferential = members.reduce(
          (sum, member) => sum + member.pointDifferential,
          0
        )
        const crystalBalls =
          members.reduce((sum, member) => sum + member.crystalBalls, 0) +
          (prophetBonusByTeam.get(name) ?? 0)
        const winPercentage = totalGames > 0 ? wins / totalGames : 0

        return {
          id: index + 1,
          name,
          playersAssigned: assignedUsers.length,
          maxPlayers: 6,
          wins,
          losses,
          ties,
          pointDifferential,
          crystalBalls,
          totalGames,
          winPercentage,
          members,
        }
      })

      return teams.sort(compareDefaultTeamStandings)
    },
    [prophetBonuses, teamMemberSortMode, userAssignments, userStandings]
  )

  const sortedTeamStandings = useMemo(() => {
    if (!sortConfig) {
      return teamStandings
    }

    const defaultRankByTeam = new Map(
      teamStandings.map((team, index) => [team.id, index + 1])
    )

    const getSortValue = (team: TeamStanding): string | number => {
      switch (sortConfig.key) {
        case 'rank':
          return defaultRankByTeam.get(team.id) ?? 0
        case 'team':
          return getLeagueTeamDisplayName(team.name)
        case 'players':
          return team.playersAssigned
        case 'wins':
          return team.wins
        case 'losses':
          return team.losses
        case 'ties':
          return team.ties
        case 'crystalBalls':
          return team.crystalBalls
        case 'pointDifferential':
          return team.pointDifferential
        case 'totalGames':
          return team.totalGames
      }
    }

    return [...teamStandings].sort((a, b) => {
      const left = getSortValue(a)
      const right = getSortValue(b)
      let comparison = 0

      if (typeof left === 'string' && typeof right === 'string') {
        comparison = left.localeCompare(right, undefined, { sensitivity: 'base' })
      } else {
        comparison = Number(left) - Number(right)
      }

      if (comparison === 0) {
        return compareDefaultTeamStandings(a, b)
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [sortConfig, teamStandings])

  useEffect(() => {
    if (!expandedTeamImage) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedTeamImage(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [expandedTeamImage])

  const handleSort = (key: SortKey) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        }
      }

      return {
        key,
        direction: key === 'rank' || key === 'team' ? 'asc' : 'desc',
      }
    })
  }

  const renderSortHeader = (
    key: SortKey,
    label: string,
    className?: string
  ) => {
    const isActive = sortConfig?.key === key
    const direction = isActive ? sortConfig.direction : null

    return (
      <th className={className}>
        <button
          type="button"
          className={`standings-sort-button${isActive ? ' active' : ''}`}
          onClick={() => handleSort(key)}
          aria-label={`Sort standings by ${label}`}
        >
          <span>{label}</span>
          <span className="sort-indicator" aria-hidden="true">
            {direction === 'asc' ? '^' : direction === 'desc' ? 'v' : ''}
          </span>
        </button>
      </th>
    )
  }

  return (
    <div className="standings">
      <h1>Standings</h1>
      {loading && <p className="standings-subtitle">Loading team standings...</p>}
      <div className="team-member-sort-toggle" aria-label="Team member sort order">
        <span>Player Order</span>
        <div className="team-member-sort-actions">
          <button
            type="button"
            className={`team-member-sort-button${teamMemberSortMode === 'performance' ? ' active' : ''}`}
            onClick={() => setTeamMemberSortMode('performance')}
          >
            Performance
          </button>
          <button
            type="button"
            className={`team-member-sort-button${teamMemberSortMode === 'draft' ? ' active' : ''}`}
            onClick={() => setTeamMemberSortMode('draft')}
          >
            Round Drafted
          </button>
        </div>
      </div>
      <div className="standings-table-wrapper">
        <table className="standings-table">
          <thead>
            <tr>
              {renderSortHeader('rank', 'Rank', 'numeric-column')}
              {renderSortHeader('team', 'Team')}
              {renderSortHeader('players', 'Players', 'numeric-column')}
              {renderSortHeader('wins', 'W', 'numeric-column')}
              {renderSortHeader('losses', 'L', 'numeric-column')}
              {renderSortHeader('ties', 'T', 'numeric-column')}
              {renderSortHeader('crystalBalls', 'Crystal Balls 🔮', 'numeric-column')}
              {renderSortHeader('pointDifferential', 'Point Diff', 'numeric-column')}
              {renderSortHeader('totalGames', 'Games', 'numeric-column')}
            </tr>
          </thead>
          <tbody>
            {sortedTeamStandings.map((team, index) => (
              <Fragment key={team.id}>
                <tr className="team-row">
                  <td className="numeric-column">{index + 1}</td>
                  <td className="player-name">
                    <div className="team-info">
                      <button
                        type="button"
                        className="team-image-placeholder"
                        onClick={() => setExpandedTeamImage(team.name)}
                        aria-label={`View ${getLeagueTeamDisplayName(team.name)} team image`}
                      >
                        <img src={getLeagueTeamImageSrc(team.name)} alt="" />
                      </button>
                      <div className="team-details">
                        <button
                          type="button"
                          className="team-name team-name-link"
                          onClick={() => navigate(`/teams/${encodeURIComponent(team.name)}/games`)}
                        >
                          {getLeagueTeamDisplayName(team.name)}
                        </button>
                        <span className="team-meta">Captain: {getLeagueTeamCaptainName(team.name)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="numeric-column">{team.playersAssigned}/{team.maxPlayers}</td>
                  <td className="numeric-column">{team.wins}</td>
                  <td className="numeric-column">{team.losses}</td>
                  <td className="numeric-column">{team.ties}</td>
                  <td className="numeric-column">{team.crystalBalls}</td>
                  <td className="numeric-column">{team.pointDifferential >= 0 ? '+' : ''}{team.pointDifferential}</td>
                  <td className="numeric-column">{team.totalGames}</td>
                </tr>
                {team.members.length > 0 ? (
                  team.members.map(member => (
                    <tr
                      key={`${team.id}-${member.id}`}
                      className="team-member-subrow clickable-member-row"
                      onClick={() => navigate(`/players/${member.id}/games`)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          navigate(`/players/${member.id}/games`)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`View game results for ${member.displayName}`}
                    >
                      <td className="numeric-column" />
                      <td className="team-member-name-cell">
                        <div className="team-member-identity">
                          <span className="team-member-draft-round">
                            {getDraftRoundLabel(member.groupPlaySlot)}
                          </span>
                          <UserAvatar
                            displayName={member.displayName}
                            discordId={member.discordId}
                            discordAvatar={member.discordAvatar}
                            size="sm"
                          />
                          <span className="team-member-name">{member.displayName}</span>
                        </div>
                      </td>
                      <td className="numeric-column" />
                      <td className="numeric-column">{member.wins}</td>
                      <td className="numeric-column">{member.losses}</td>
                      <td className="numeric-column">{member.ties}</td>
                      <td className="numeric-column">{member.crystalBalls}</td>
                      <td className="team-member-diff numeric-column">
                        {member.pointDifferential >= 0 ? '+' : ''}
                        {member.pointDifferential}
                      </td>
                      <td className="numeric-column">{member.totalGames}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="team-member-subrow team-member-empty-row">
                    <td className="numeric-column" />
                    <td className="team-member-empty">No team members assigned yet</td>
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                    <td className="numeric-column" />
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {expandedTeamImage && (
        <div
          className="team-image-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${getLeagueTeamDisplayName(expandedTeamImage)} image preview`}
          onClick={() => setExpandedTeamImage(null)}
        >
          <button
            type="button"
            className="team-image-modal-content"
            onClick={event => event.stopPropagation()}
            aria-label="Team image preview"
          >
            <img
              src={getLeagueTeamImageSrc(expandedTeamImage)}
              alt={`${getLeagueTeamDisplayName(expandedTeamImage)} team artwork`}
            />
            <span>{getLeagueTeamDisplayName(expandedTeamImage)}</span>
          </button>
          <button
            type="button"
            className="team-image-modal-close"
            onClick={() => setExpandedTeamImage(null)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}

